import { stripFuturesExpiration } from "../shared/utils";

// ─── Topstep X "tabbed fills" paste parser ───────────────────────────────────
// A second Topstep X export: one row per order fill (not per completed trade).
// Rows are antichronological (most recent first). Each tabbed row is followed
// by a "$x" fee line, and — only on the fill that closes a trade — a second
// "$x" Profit line.
//
// Tabbed row columns (0-based tab-cell index):
//   0: "YYYY-MM-DD HH:MM:SS CT"   1: date   2: orderId
//   3: Buy|Sell   4: qty   5: symbol   6: price

// A tabbed fill row: cell[0] = timestamp + CT, cell[3] = Buy/Sell.
const FILL_ROW_RE =
  /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}\s+CT\t/;

/**
 * Parse a Topstep "YYYY-MM-DD HH:MM:SS" wall-clock string stamped "CT"
 * (America/Chicago) into a correct absolute instant.
 *
 * Topstep exports the trader's Chicago wall-clock time. Feeding that string to
 * `new Date()` would interpret it as the *machine's* local time, storing a
 * wrong instant — the session timeline and clustering (which split on absolute
 * 2h gaps) would then be off. Chicago alternates CST (UTC-6) / CDT (UTC-5), so
 * the offset is resolved per-date with Intl rather than hard-coded.
 *
 * The returned Date is a genuine instant; the UI renders it in the browser's
 * timezone — i.e. Europe/Paris for a French user.
 */
export function parseChicagoTimestamp(wallClock: string): Date {
  // Interpret the wall-clock components as if they were UTC, then correct by
  // the real Chicago offset for that calendar moment.
  const m = wallClock.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/,
  );
  if (!m) return new Date(wallClock); // unrecognised — fall back, caller validates
  const [, y, mo, d, h, mi, s] = m.map(Number) as unknown as number[];
  const asUtc = Date.UTC(y, mo - 1, d, h, mi, s);

  // Offset (minutes) that America/Chicago was at for this instant. Computed by
  // formatting the UTC instant in the Chicago zone and diffing the wall clocks.
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(asUtc));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const chicagoWall = Date.UTC(
    get("year"), get("month") - 1, get("day"),
    get("hour") % 24, get("minute"), get("second"),
  );
  const offsetMs = chicagoWall - asUtc; // e.g. -5h in CDT, -6h in CST
  return new Date(asUtc - offsetMs);
}

// Topstep's all-in cost per contract per side: broker commission ($0.37) plus
// CME exchange + NFA clearing fees (~$0.25). The tabbed export only lists the
// broker part, so the all-in rate is applied here instead. A round-trip trade
// of N contracts is charged on N entry + N exit contract-sides.
const TOPSTEP_COST_PER_CONTRACT_SIDE = 0.62;

/** True when the paste looks like the tabbed fills format. */
export function isTopstepXFillsFormat(text: string): boolean {
  const lines = text.split("\n");
  return lines.some((line) => {
    if (!FILL_ROW_RE.test(line)) return false;
    const cells = line.split("\t");
    const side = (cells[3] ?? "").trim().toLowerCase();
    return side === "buy" || side === "sell";
  });
}

/** One order fill, after grouping a tabbed row with its $ lines. */
export interface TopstepXFill {
  time: Date;
  orderId: string;
  side: "Buy" | "Sell";
  qty: number;
  symbol: string;
  price: number;
  /** Fee for this fill (Topstep exports it negative, e.g. -0.37). */
  fee: number;
  /** Gross Profit of the trade closed by this fill, or null for an opening fill. */
  profit: number | null;
}

// "$-0.37" → -0.37 ; "$13.50" → 13.5. Returns 0 for an unparseable string.
function parseMoney(s: string): number {
  const n = parseFloat(s.replace(/[$,]/g, ""));
  return isFinite(n) ? n : 0;
}

// A "$..." money line (fee or profit).
const MONEY_LINE_RE = /^\$-?[\d.,]+$/;

/**
 * Group the tabbed paste into fills. Each tabbed row starts a fill; the
 * following "$x" line is its fee, and a second "$x" line (if any, before the
 * next tabbed row) is its Profit. Blank lines are ignored.
 */
export function parseTopstepXFills(text: string): TopstepXFill[] {
  if (!text) return [];
  const lines = text.split("\n");
  const fills: TopstepXFill[] = [];
  let current: TopstepXFill | null = null;
  let moneySeen = 0; // how many "$x" lines attached to `current` so far

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === "") continue;

    if (FILL_ROW_RE.test(rawLine)) {
      const cells = rawLine.split("\t");
      const side = (cells[3] ?? "").trim();
      if (side !== "Buy" && side !== "Sell") {
        current = null;
        continue;
      }
      const qty = parseFloat((cells[4] ?? "").trim());
      const price = parseFloat((cells[6] ?? "").trim());
      const symbol = (cells[5] ?? "").trim();
      // A row with an unparseable qty/price or empty symbol is malformed —
      // drop it rather than emitting a fill that would poison FIFO matching.
      if (!isFinite(qty) || !isFinite(price) || symbol === "") {
        current = null;
        continue;
      }
      // cell[0] is "YYYY-MM-DD HH:MM:SS CT". The " CT" suffix is dropped and
      // the Chicago wall-clock is converted to a correct absolute instant, so
      // the stored time, session timeline and 2h-gap clustering all line up
      // with reality (and render in the user's timezone, e.g. Europe/Paris).
      const ts = (cells[0] ?? "").replace(/\s+CT$/, "").trim();
      current = {
        time: parseChicagoTimestamp(ts),
        orderId: (cells[2] ?? "").trim(),
        side,
        qty,
        symbol,
        price,
        fee: 0,
        profit: null,
      };
      moneySeen = 0;
      fills.push(current);
      continue;
    }

    if (current && MONEY_LINE_RE.test(line)) {
      if (moneySeen === 0) {
        current.fee = parseMoney(line);
      } else if (moneySeen === 1) {
        current.profit = parseMoney(line);
      }
      moneySeen++;
      continue;
    }

    // Any other non-empty line ends the current fill's $-line run.
    current = null;
  }

  return fills;
}

/**
 * A reconstructed trade. Structurally identical to AddTradeModal's
 * ParsedTradovateTrade, so the array is directly assignable to it.
 */
export interface TopstepXFillTrade {
  symbol: string;
  qty: number;
  entryPrice: number;
  exitPrice: number;
  entryTime: Date;
  exitTime: Date;
  pnl: number;
  type: "long" | "short";
  /**
   * All-in Topstep cost for the trade: $0.62 per contract per side, charged on
   * both entry and exit sides. Stored negative.
   */
  commission: number;
}

export interface TopstepXFillsResult {
  trades: TopstepXFillTrade[];
  /** Open lots left over at the end of parsing (incomplete paste). */
  unmatchedCount: number;
}

// One open contract in the FIFO queue.
interface OpenLot {
  side: "Buy" | "Sell";
  price: number;
  time: Date;
}

/**
 * Parse the tabbed Topstep X fills paste into completed trades using FIFO
 * position matching. Returns null when the text is not this format.
 *
 * Algorithm: replay fills oldest-first. Maintain a FIFO queue of open
 * single-contract lots (all the same side at any moment). An opposite-side
 * fill closes lots FIFO and emits ONE trade for that single closing fill —
 * matching Topstep's trade count (one trade per closing fill). A scaled-out
 * position closed across several fills becomes several trades, each carrying
 * its own fill's Profit as the trade P&L. A fill may also close the current
 * position AND open a new one in the other direction (reversal) — closing is
 * handled first, then the leftover quantity opens new lots.
 */
export function parseTopstepXFillsPaste(
  text: string,
): TopstepXFillsResult | null {
  if (!isTopstepXFillsFormat(text)) return null;

  // Paste is antichronological; replay oldest-first.
  const fills = parseTopstepXFills(text)
    .slice()
    .sort((a, b) => a.time.getTime() - b.time.getTime());

  const trades: TopstepXFillTrade[] = [];
  // FIFO queue of open contracts. All lots share the same side at any time.
  let queue: OpenLot[] = [];

  for (const fill of fills) {
    if (fill.qty === 0) continue; // guard: a zero-qty fill carries no contracts
    let remaining = fill.qty;

    // Closing phase: an opposite-side fill closes open lots FIFO and emits
    // exactly ONE trade for this single closing fill — Topstep counts one trade per
    // closing fill, even when a scaled-out position closes across several.
    if (remaining > 0 && queue.length > 0 && queue[0].side !== fill.side) {
      const openSide = queue[0].side;
      const consumed: OpenLot[] = [];
      while (remaining > 0 && queue.length > 0 && queue[0].side === openSide) {
        consumed.push(queue.shift()!);
        remaining -= 1;
      }
      const avgEntryPrice =
        consumed.reduce((s, l) => s + l.price, 0) / consumed.length;
      trades.push({
        symbol: stripFuturesExpiration(fill.symbol),
        qty: consumed.length,
        entryPrice: avgEntryPrice,
        exitPrice: fill.price,
        entryTime: consumed[0].time,
        exitTime: fill.time,
        pnl: fill.profit ?? 0,
        type: openSide === "Buy" ? "long" : "short",
        commission: -TOPSTEP_COST_PER_CONTRACT_SIDE * 2 * consumed.length,
      });
    }

    // Opening phase: leftover quantity opens new lots.
    while (remaining > 0) {
      queue.push({ side: fill.side, price: fill.price, time: fill.time });
      remaining -= 1;
    }
  }

  return { trades, unmatchedCount: queue.length };
}
